import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Sidebar from '../../components/feature/Sidebar';
import VideoEmbed from '../blog/components/VideoEmbed';
import ContentRenderer from './components/ContentRenderer';
import { supabase, BlogPost } from '../../lib/supabase';
import DiagnosticFlowchart from '../blog/[slug]/components/DiagnosticFlowchart';
import TroubleChecklist from '../blog/[slug]/components/TroubleChecklist';
import StepByStepGuide from './components/StepByStepGuide';

// 目次の項目の型定義
interface TocItem {
  id: string;
  text: string;
  level: number;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<BlogPost | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [toc, setToc] = useState<TocItem[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);

  // 記事ごとのステップバイステップガイドデータ
  const stepGuides: Record<string, { title: string; steps: Array<{ number: number; title: string; description: string; icon: string; tips?: string }>; color: 'cyan' | 'teal' | 'blue' | 'purple' | 'amber' }> = {
    'streaming-audio-trouble': {
      title: '音声トラブル解決の手順',
      color: 'cyan',
      steps: [
        {
          number: 1,
          title: '音声デバイスの確認',
          description: 'マイクが正しく接続され、認識されているか確認します',
          icon: 'ri-mic-line',
          tips: 'Windowsの場合：設定 > システム > サウンド > 入力デバイスで確認。Macの場合：システム環境設定 > サウンド > 入力で確認できます。'
        },
        {
          number: 2,
          title: '音量レベルの調整',
          description: '入力音量が適切な範囲（-12dB〜-6dB）に設定されているか確認',
          icon: 'ri-volume-up-line',
          tips: 'OBSの音声ミキサーで、緑色のゲージが黄色に時々触れる程度が理想的です。赤色に入ると音割れの原因になります。'
        },
        {
          number: 3,
          title: 'ノイズ抑制の設定',
          description: 'OBSのフィルタ機能でノイズ抑制とゲインを設定',
          icon: 'ri-equalizer-line',
          tips: 'ノイズ抑制は-30dB程度、ゲインは必要に応じて+5dB〜+10dB程度に設定します。過度な設定は音質劣化の原因になります。'
        },
        {
          number: 4,
          title: 'テスト配信で確認',
          description: '実際に短時間のテスト配信を行い、音声品質を確認',
          icon: 'ri-play-circle-line',
          tips: 'YouTubeの限定公開やTwitchのテストチャンネルを使用して、録画しながらテスト配信を行うと後で確認できます。'
        },
        {
          number: 5,
          title: '最終調整と記録',
          description: '最適な設定値を記録し、次回以降も同じ設定を使用',
          icon: 'ri-save-line',
          tips: 'OBSのシーンコレクションとプロファイルを保存しておくと、設定を簡単に呼び出せます。'
        }
      ]
    },
    'camera-not-recognized': {
      title: 'カメラ認識トラブル解決の手順',
      color: 'teal',
      steps: [
        {
          number: 1,
          title: '物理接続の確認',
          description: 'USBケーブルが正しく接続され、ポートに問題がないか確認',
          icon: 'ri-usb-line',
          tips: 'USB 3.0ポート（青色）を使用すると安定します。ハブを使用している場合は、直接PCに接続してみてください。'
        },
        {
          number: 2,
          title: 'デバイスマネージャーで確認',
          description: 'Windowsのデバイスマネージャーでカメラが認識されているか確認',
          icon: 'ri-settings-3-line',
          tips: '「イメージングデバイス」または「カメラ」の項目を確認。黄色い警告マークがある場合はドライバーの問題です。'
        },
        {
          number: 3,
          title: 'ドライバーの更新',
          description: '最新のカメラドライバーをインストールまたは更新',
          icon: 'ri-download-cloud-line',
          tips: 'メーカーの公式サイトから最新ドライバーをダウンロード。Windowsの自動更新だけでは不十分な場合があります。'
        },
        {
          number: 4,
          title: '他のアプリの終了',
          description: 'カメラを使用している可能性のある他のアプリを終了',
          icon: 'ri-close-circle-line',
          tips: 'Zoom、Skype、Teams、Discordなどのビデオ通話アプリが起動していないか確認してください。'
        },
        {
          number: 5,
          title: 'OBSで再設定',
          description: 'OBSのビデオキャプチャデバイスを削除して再追加',
          icon: 'ri-refresh-line',
          tips: 'ソースを右クリック > 削除 > 新規追加で、デバイスを選び直すと認識されることがあります。'
        }
      ]
    },
    'streaming-bandwidth-optimization': {
      title: '帯域最適化の手順',
      color: 'blue',
      steps: [
        {
          number: 1,
          title: '現在の回線速度を測定',
          description: 'Speedtest.netなどで実際のアップロード速度を確認',
          icon: 'ri-speed-line',
          tips: '配信に必要な速度の1.5倍以上のアップロード速度があることを確認してください。例：6Mbps配信なら9Mbps以上。'
        },
        {
          number: 2,
          title: 'ビットレートの設定',
          description: '回線速度の70%程度を目安にビットレートを設定',
          icon: 'ri-dashboard-line',
          tips: '1080p/60fps: 6000kbps、1080p/30fps: 4500kbps、720p/60fps: 4500kbps、720p/30fps: 3000kbpsが目安です。'
        },
        {
          number: 3,
          title: 'エンコーダーの選択',
          description: 'GPUエンコーダー（NVENC/QuickSync）を使用してCPU負荷を軽減',
          icon: 'ri-cpu-line',
          tips: 'NVIDIAのGPUがあればNVENC、IntelのCPUがあればQuickSyncを選択。x264はCPU負荷が高いため高性能PCが必要です。'
        },
        {
          number: 4,
          title: '有線接続への切り替え',
          description: 'Wi-Fiではなく有線LANで接続して安定性を向上',
          icon: 'ri-router-line',
          tips: 'Cat6以上のLANケーブルを使用し、ルーターとPCを直接接続すると最も安定します。'
        },
        {
          number: 5,
          title: 'テスト配信で確認',
          description: '実際に配信して、ドロップフレームが発生しないか確認',
          icon: 'ri-eye-line',
          tips: 'OBSの下部ステータスバーで「ドロップフレーム」が0.5%以下であれば問題ありません。'
        }
      ]
    },
    'obs-studio-beginner-guide': {
      title: 'OBS Studio初期設定の手順',
      color: 'purple',
      steps: [
        {
          number: 1,
          title: '自動設定ウィザードの実行',
          description: 'OBS起動時の自動設定ウィザードで基本設定を行う',
          icon: 'ri-magic-line',
          tips: '「配信用に最適化」を選択し、使用する配信プラットフォームを指定してください。'
        },
        {
          number: 2,
          title: '映像ソースの追加',
          description: 'ゲームキャプチャ、ウィンドウキャプチャ、カメラなどを追加',
          icon: 'ri-add-circle-line',
          tips: 'ゲーム配信の場合は「ゲームキャプチャ」、デスクトップ全体なら「画面キャプチャ」を使用します。'
        },
        {
          number: 3,
          title: '音声ソースの設定',
          description: 'マイクとデスクトップ音声を適切に設定',
          icon: 'ri-headphone-line',
          tips: '音声ミキサーで各ソースの音量バランスを調整。マイクは-12dB〜-6dB、デスクトップ音声は-18dB〜-12dB程度が目安です。'
        },
        {
          number: 4,
          title: 'シーンの構成',
          description: '配信開始画面、メイン画面、休憩画面などのシーンを作成',
          icon: 'ri-layout-grid-line',
          tips: '最低でも「配信開始前」「配信中」「休憩中」「配信終了」の4つのシーンを用意すると便利です。'
        },
        {
          number: 5,
          title: 'ホットキーの設定',
          description: 'シーン切り替えやミュートなどのショートカットキーを設定',
          icon: 'ri-keyboard-line',
          tips: 'テンキーやファンクションキーを使うと、ゲーム中でも誤操作なく切り替えられます。'
        }
      ]
    },
    'streaming-lighting-basics': {
      title: '照明設定の手順',
      color: 'amber',
      steps: [
        {
          number: 1,
          title: 'キーライトの配置',
          description: 'メインの照明を顔の斜め前45度、高さ45度に配置',
          icon: 'ri-sun-line',
          tips: '最も明るい光源で、顔全体を均一に照らします。色温度は5000K〜5600K（昼光色）が自然です。'
        },
        {
          number: 2,
          title: 'フィルライトの配置',
          description: 'キーライトの反対側に、影を和らげる補助光を配置',
          icon: 'ri-contrast-2-line',
          tips: 'キーライトの50%程度の明るさに設定。影を完全に消すのではなく、和らげる程度が自然です。'
        },
        {
          number: 3,
          title: 'バックライトの配置',
          description: '背後から照らして、背景との分離感を出す',
          icon: 'ri-flashlight-line',
          tips: '頭や肩のラインを強調し、立体感を出します。色温度を変えて（青系など）アクセントにするのも効果的です。'
        },
        {
          number: 4,
          title: 'カメラ設定の調整',
          description: '照明に合わせてカメラの露出、ホワイトバランスを調整',
          icon: 'ri-camera-line',
          tips: 'オートではなくマニュアル設定にすると、照明の変化に影響されず安定した映像になります。'
        },
        {
          number: 5,
          title: 'テスト撮影で確認',
          description: '実際に撮影して、顔色や明るさが自然か確認',
          icon: 'ri-video-line',
          tips: 'スマホで撮影して確認するのも有効。画面越しと実際の見え方は異なるため、必ず録画して確認しましょう。'
        }
      ]
    },
    'streaming-emergency-manual': {
      title: '緊急対応の手順',
      color: 'cyan',
      steps: [
        {
          number: 1,
          title: '問題の特定',
          description: '映像・音声・接続のどこに問題があるか素早く判断',
          icon: 'ri-search-eye-line',
          tips: 'OBSのステータスバーで「ドロップフレーム」「CPU使用率」「ビットレート」を確認。視聴者のコメントも参考にします。'
        },
        {
          number: 2,
          title: '一時的な対処',
          description: '「休憩中」画面に切り替えて、視聴者に状況を説明',
          icon: 'ri-pause-circle-line',
          tips: '事前に「技術的な問題が発生しました。少々お待ちください」というテキストを含む休憩画面を用意しておきましょう。'
        },
        {
          number: 3,
          title: 'バックアップ手段の実行',
          description: '予備のシーン、デバイス、または配信設定に切り替え',
          icon: 'ri-shield-check-line',
          tips: '低ビットレートの予備プロファイルを用意しておくと、回線トラブル時に素早く切り替えられます。'
        },
        {
          number: 4,
          title: '復旧作業',
          description: '問題の原因を解決し、通常配信に戻す',
          icon: 'ri-tools-line',
          tips: '解決に時間がかかる場合は、一度配信を終了して再開する方が視聴者の印象が良い場合もあります。'
        },
        {
          number: 5,
          title: '事後の記録と対策',
          description: '発生した問題と対処法を記録し、再発防止策を立てる',
          icon: 'ri-file-list-line',
          tips: 'トラブルログを作成し、「いつ・何が・どう対処したか」を記録。次回の配信前チェックリストに追加します。'
        }
      ]
    },
    'encoder-comparison-guide': {
      title: 'エンコーダー選択の手順',
      color: 'teal',
      steps: [
        {
          number: 1,
          title: 'PCスペックの確認',
          description: 'CPU、GPU、メモリの性能を確認',
          icon: 'ri-computer-line',
          tips: 'タスクマネージャー（Ctrl+Shift+Esc）でCPUとGPUの型番を確認。NVIDIAならGeForce GTX 1650以上、IntelならCore i5以上が目安です。'
        },
        {
          number: 2,
          title: '配信内容の分析',
          description: 'ゲーム配信か雑談配信か、動きの激しさを考慮',
          icon: 'ri-gamepad-line',
          tips: '動きの激しいFPSゲームは高ビットレート+GPUエンコーダー、雑談配信は低ビットレート+x264でも十分です。'
        },
        {
          number: 3,
          title: 'エンコーダーの選択',
          description: 'x264、NVENC、QuickSyncから最適なものを選択',
          icon: 'ri-cpu-line',
          tips: 'NVIDIA GPU所有 → NVENC、Intel CPU（GPU内蔵）→ QuickSync、高性能CPU → x264の順で検討します。'
        },
        {
          number: 4,
          title: 'プリセットの設定',
          description: '品質と負荷のバランスを考慮してプリセットを選択',
          icon: 'ri-settings-4-line',
          tips: 'NVENCは「Quality」、x264は「veryfast」〜「medium」が一般的。高品質を求めるなら「slower」も検討します。'
        },
        {
          number: 5,
          title: 'テストと比較',
          description: '実際に配信して、画質とPC負荷を比較',
          icon: 'ri-bar-chart-line',
          tips: '同じシーンを複数のエンコーダーで録画し、画質とCPU/GPU使用率を比較。最もバランスの良い設定を選びます。'
        }
      ]
    }
  };

  const currentStepGuide = article ? stepGuides[article.slug] : null;

  useEffect(() => {
    if (slug) {
      fetchArticle();
    }
  }, [slug]);

  // 記事取得後に目次を生成
  useEffect(() => {
    if (article?.content && contentRef.current) {
      // DOMが完全にレンダリングされるまで待つ（より長い遅延）
      const timer = setTimeout(() => {
        if (!contentRef.current) return;
        
        const headings = contentRef.current.querySelectorAll('h2, h3, h4');
        const tocItems: TocItem[] = Array.from(headings).map((heading, index) => {
          const level = parseInt(heading.tagName.substring(1));
          const text = heading.textContent || '';
          const id = `heading-${index}`;
          
          // 見出しにIDを付与
          heading.id = id;
          // スクロール時のオフセット用のクラスを追加
          heading.classList.add('scroll-mt-28');
          
          return { id, text, level };
        });
        
        console.log('目次生成完了:', tocItems); // デバッグ用
        console.log('見出し要素:', Array.from(headings).map(h => ({ id: h.id, text: h.textContent }))); // デバッグ用
        
        setToc(tocItems);
      }, 500); // 300msから500msに変更
      
      return () => clearTimeout(timer);
    }
  }, [article, loading]);

  // 目次項目クリック時のスクロール処理
  const scrollToHeading = (id: string) => {
    console.log('クリックされたID:', id); // デバッグ用
    const element = document.getElementById(id);
    console.log('見つかった要素:', element); // デバッグ用
    
    if (element) {
      const headerOffset = 120;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    } else {
      console.error('要素が見つかりません:', id);
    }
  };

  const fetchArticle = async () => {
    try {
      setLoading(true);
      
      const { data: articleData, error: articleError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .maybeSingle();

      if (articleError) throw articleError;
      
      if (articleData) {
        setArticle(articleData);

        const { data: relatedData, error: relatedError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('category', articleData.category)
          .eq('is_published', true)
          .neq('id', articleData.id)
          .order('published_date', { ascending: false })
          .limit(2);

        if (relatedError) throw relatedError;
        setRelatedArticles(relatedData || []);
      } else {
        setArticle(null);
      }
    } catch (error) {
      console.error('記事の取得に失敗しました:', error);
      setArticle(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '.');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 text-orange-500">
            <i className="ri-loader-4-line text-6xl animate-spin"></i>
          </div>
          <p className="text-gray-600">読み込み中...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="max-w-7xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 text-gray-300">
            <i className="ri-article-line text-6xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">記事が見つかりません</h1>
          <p className="text-gray-600 mb-8">お探しの記事は存在しないか、削除された可能性があります。</p>
          <Link
            to="/blog"
            className="inline-flex items-center px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            ブログ一覧に戻る
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <nav className="mb-6">
          <ol className="flex items-center text-sm text-gray-500">
            <li>
              <Link to="/" className="hover:text-orange-500 cursor-pointer">ホーム</Link>
            </li>
            <li className="mx-2">/</li>
            <li>
              <Link to="/blog" className="hover:text-orange-500 cursor-pointer">ブログ</Link>
            </li>
            <li className="mx-2">/</li>
            <li className="text-gray-900 truncate max-w-xs">{article.title}</li>
          </ol>
        </nav>

        <div className="flex flex-col lg:flex-row gap-10">
          <div className="flex-1 min-w-0">
            <article className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="w-full h-96 md:h-[480px] bg-gray-200">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="px-6 md:px-10 lg:px-12 py-8 md:py-10">
                {/* メタ情報 */}
                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <time className="text-gray-400 text-sm">{formatDate(article.published_date)}</time>
                  <span className="bg-gray-900 text-white text-xs px-3 py-1 rounded font-medium">
                    {article.category}
                  </span>
                  <div className="flex items-center text-gray-400 text-sm">
                    <i className="ri-time-line mr-1"></i>
                    {article.reading_time}分で読めます
                  </div>
                </div>

                {/* タイトル */}
                <h1 className="text-2xl md:text-[1.75rem] font-extrabold text-gray-900 mb-8 leading-relaxed tracking-tight">
                  {article.title}
                </h1>

                {/* 目次 */}
                {toc.length > 0 && (
                  <div className="bg-[#fafafa] rounded-lg p-6 md:p-8 mb-10 border border-gray-200">
                    <h2 className="text-base font-bold text-gray-900 mb-5 flex items-center gap-2 !border-none !p-0 !m-0">
                      <span className="w-7 h-7 flex items-center justify-center bg-[#f5c518] text-white rounded text-xs font-bold">目</span>
                      目次
                    </h2>
                    <nav>
                      <ol className="space-y-0">
                        {toc.map((item, index) => (
                          <li
                            key={item.id}
                            className={`${item.level === 2 ? 'border-b border-gray-200 last:border-b-0' : ''}`}
                            style={{ paddingLeft: item.level > 2 ? `${(item.level - 2) * 1.2}rem` : '0' }}
                          >
                            <button
                              type="button"
                              onClick={() => scrollToHeading(item.id)}
                              className={`text-left w-full transition-colors cursor-pointer ${
                                item.level === 2
                                  ? 'py-3 text-[0.92rem] font-semibold text-gray-800 hover:text-cyan-600'
                                  : 'py-1.5 text-[0.85rem] text-gray-500 hover:text-cyan-600'
                              }`}
                            >
                              {item.level === 2 && (
                                <span className="inline-flex items-center justify-center w-5 h-5 bg-[#f5c518] text-white text-[10px] font-bold rounded-full mr-2.5">
                                  {toc.filter((t, i) => t.level === 2 && i <= toc.indexOf(item)).length}
                                </span>
                              )}
                              {item.level > 2 && (
                                <span className="text-gray-300 mr-2">─</span>
                              )}
                              {item.text}
                            </button>
                          </li>
                        ))}
                      </ol>
                    </nav>
                  </div>
                )}

                {article.video_url && article.video_type && (
                  <VideoEmbed
                    url={article.video_url}
                    type={article.video_type as 'youtube' | 'vimeo'}
                    title={article.title}
                  />
                )}

                {/* 本文 */}
                <div ref={contentRef} className="mb-4">
                  <ContentRenderer content={article.content} slug={article.slug} />
                </div>

                {/* ステップバイステップガイド */}
                {currentStepGuide && (
                  <StepByStepGuide
                    title={currentStepGuide.title}
                    steps={currentStepGuide.steps}
                    color={currentStepGuide.color}
                  />
                )}

                {/* チェックリスト */}
                <TroubleChecklist />

                {/* シェアボタン */}
                <div className="mt-14 pt-8 border-t-2 border-gray-100">
                  <p className="text-gray-900 font-bold text-base mb-5">この記事が役に立ったら、ぜひシェアしてください！</p>
                  <div className="flex gap-3">
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-11 h-11 flex items-center justify-center bg-gray-100 text-gray-500 rounded-full hover:bg-[#1DA1F2] hover:text-white transition-all cursor-pointer"
                    >
                      <i className="ri-twitter-x-line text-lg"></i>
                    </a>
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-11 h-11 flex items-center justify-center bg-gray-100 text-gray-500 rounded-full hover:bg-[#1877F2] hover:text-white transition-all cursor-pointer"
                    >
                      <i className="ri-facebook-fill text-lg"></i>
                    </a>
                    <a
                      href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(article.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-11 h-11 flex items-center justify-center bg-gray-100 text-gray-500 rounded-full hover:bg-[#0A66C2] hover:text-white transition-all cursor-pointer"
                    >
                      <i className="ri-linkedin-fill text-lg"></i>
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                      }}
                      className="w-11 h-11 flex items-center justify-center bg-gray-100 text-gray-500 rounded-full hover:bg-gray-700 hover:text-white transition-all cursor-pointer"
                    >
                      <i className="ri-link text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {/* 関連記事 */}
            {relatedArticles.length > 0 && (
              <div className="mt-10">
                <h2 className="text-lg font-bold text-gray-900 mb-6 pb-3 border-b-2 border-gray-900">関連記事</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow group cursor-pointer"
                    >
                      <div className="w-full h-44 bg-gray-200 overflow-hidden">
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-gray-400 text-xs">{formatDate(related.published_date)}</span>
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{related.category}</span>
                        </div>
                        <h3 className="text-gray-900 font-bold text-sm leading-relaxed line-clamp-2 group-hover:text-cyan-600 transition-colors">
                          {related.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 text-center">
              <Link
                to="/blog"
                className="inline-flex items-center px-8 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors cursor-pointer font-medium text-sm whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                ブログ一覧に戻る
              </Link>
            </div>
          </div>

          <div className="lg:flex-shrink-0">
            <Sidebar />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
